// Which §4.2 actions a stock unit's status allows (rev-7, UA-inventory phase 2).
//
// The audit found Cut, Re-measure, Move, Reserve, Quarantine and Retire all
// enabled on a RETIRED unit: every press ended in a 422 the user had no way to
// foresee. The card now draws each action its role holds and disables the ones
// the unit's status rules out, with one line saying why. The server stays the
// authority (spec 06 §2.3 and each row of §5); this table only mirrors the
// status sets the services check, so a button never invites a refusal that the
// status alone already decides:
//
//   * cut, re-measure, reserve, quarantine — `filled` or `in_use`
//     (`CUTTABLE`, `REMEASURABLE`, the reservation guard, `QUARANTINABLE`);
//   * release — `quarantined` only;
//   * retire — `empty`, `filled` or `in_use` (a drained one; residual stock is
//     the server's refusal to explain);
//   * move (a `repack` relocation) — any unit still in the building and not on
//     a transfer.
//
// `consumed`, `shipped` and `retired` are terminal: nothing on the card applies.

import { enumLabel } from "../../../../lib/enum-label";
import type { TFunction } from "i18next";

export type UnitStatus =
  "empty" | "filled" | "in_use" | "in_transit" | "quarantined" | "consumed" | "shipped" | "retired";

export type UnitCardAction =
  "cut" | "remeasure" | "move" | "reserve" | "quarantine" | "release" | "retire";

/** Why an action is not available: an i18n key under `inventory.unitCard.blocked`. */
export type UnitBlockReason = "terminal" | "inTransit" | "empty" | "quarantined" | "notHeld";

const TERMINAL: ReadonlySet<string> = new Set(["consumed", "shipped", "retired"]);
const LIVE: ReadonlySet<string> = new Set(["filled", "in_use"]);

/** True when nothing on the card can change the unit any more. */
export function isTerminalUnit(status: string): boolean {
  return TERMINAL.has(status);
}

/**
 * The reason `action` cannot run on a unit in `status`, or `null` when the
 * status allows it. A status the client does not know allows everything, so
 * a new server status never hides an action the server would accept.
 */
export function unitActionBlock(status: string, action: UnitCardAction): UnitBlockReason | null {
  if (TERMINAL.has(status)) return "terminal";
  if (status === "in_transit") return "inTransit";
  const known = LIVE.has(status) || status === "empty" || status === "quarantined";
  if (!known) return null;
  switch (action) {
    case "move":
      return null;
    case "release":
      return status === "quarantined" ? null : "notHeld";
    case "retire":
      return status === "quarantined" ? "quarantined" : null;
    case "cut":
    case "remeasure":
    case "reserve":
    case "quarantine":
      if (LIVE.has(status)) return null;
      return status === "empty" ? "empty" : "quarantined";
  }
}

/** The one line under the action bar, in the reader's language. A status sits
 * inside running text there, so its label is lower-cased for the locale:
 * "This item is retired", never "This item is Retired". */
export function blockedReasonText(
  t: TFunction,
  reason: UnitBlockReason,
  status: string,
  locale: string,
): string {
  const label = enumLabel(t, "inventory.vocab.status", status);
  return t(`inventory.unitCard.blocked.${reason}`, {
    status: label.toLocaleLowerCase(locale),
  });
}
